<script setup lang="ts">
import { onMounted, onBeforeUnmount, nextTick } from 'vue'

import { useClient } from '../../../../../utils/client-only'
import { useChipScroller } from '../../../../../composables/use-chip-scroller'
import Icon from '../../../../../components/shared/Icon.vue'

type DayChip = {
  offset: number,
  label: string,
  dateLabel: string,
  isActive: boolean
}

defineProps<{
  headline?: string,
  dayChips: DayChip[]
}>()

const emit = defineEmits<{
  'select-day': [offset: number]
}>()

const { isClient } = useClient()

const {
  el: dayChipsEl,
  canScrollPrev: daysCanScrollPrev,
  canScrollNext: daysCanScrollNext,
  isDragging: isDraggingDays,
  consumeDragMoved: consumeDaysDragMoved,
  updateScrollState: updateDaysScrollState,
  scrollActiveChipToStart,
  scrollByChip: scrollDays,
  startDrag: startDaysDrag,
  endDrag: endDaysDrag
} = useChipScroller()

// Suppress the click that follows a drag so a swipe doesn't also select the chip underneath the pointer
function handleDayClick (offset: number) {
  if (consumeDaysDragMoved()) return
  emit('select-day', offset)
}

onMounted(() => {
  // the active day isn't necessarily the first chip (the range extends 4 days into the past) - push it to
  // the left edge right away so it's obvious there are more (future) days to swipe to, and the prev/next
  // buttons reflect the real scroll state instead of staying disabled until the user swipes once themselves
  nextTick(() => {
    scrollActiveChipToStart()
    updateDaysScrollState()
  })
  window.addEventListener('resize', updateDaysScrollState)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateDaysScrollState)
  endDaysDrag()
})
</script>

<template>
  <section class="bx-search__header bx-typo--search-header bx-filters bx-filters--thematic">
    <h2 v-if="headline">
      {{ headline }}
    </h2>
    <div class="bx-air-date-navi">
      <button type="button"
              aria-label="Vorherigen Tag anzeigen"
              :disabled="isClient && !daysCanScrollPrev"
              @click="scrollDays(-1)">
        <Icon name="arrow_pager-prev" />
      </button>
      <div ref="dayChipsEl"
           class="bx-epg-filter__chips bx-epg-filter__chips--days"
           :class="{ 'bx-epg-filter__chips--dragging': isDraggingDays }"
           @scroll="updateDaysScrollState"
           @pointerdown="startDaysDrag">
        <button v-for="chip in dayChips"
                :key="chip.offset"
                type="button"
                class="bx-epg-chip bx-epg-chip--day"
                :class="{ 'bx-epg-chip--active': isClient && chip.isActive }"
                @click="handleDayClick(chip.offset)">
          <span class="bx-epg-chip__day-name">{{ chip.label }}</span>
          <span class="bx-epg-chip__day-date">{{ chip.dateLabel }}</span>
        </button>
      </div>
      <button type="button"
              aria-label="Nächsten Tag anzeigen"
              :disabled="isClient && !daysCanScrollNext"
              @click="scrollDays(1)">
        <Icon name="arrow_pager-next" />
      </button>
    </div>
  </section>
</template>
